<template>
    <b-row id="UserManagerContainer" class="w-100 h-100 p-0">
        <!-- Affichage du menu de gestion des utilisateur avec option DCX Relève-->
        <b-card no-body class="rounded-0 w-100 h-100 pt-0" header-tag="header">
            <template #header>
                <!-- $t('UsersManagement') / Gestion des utilisateurs -->
                <h4 class="font-weight-semibold px-4 pb-0 m-0">{{ getTitle()}}</h4>
            </template>

            <b-tabs v-if="!userCreateEdit" card content-class="ml-2 flex-grow-1 overflow-auto bg-white" :active-nav-item-class="'text-' + Variant" nav-class="p-0"
                class="d-flex flex-column w-100 h-100 px-4 mb-2" >
                <template #tabs-end>
                    <b-row class="flex-grow-1 justify-content-end mr-0">
                        <!-- Activation automatique lors de l'ajout d'un téléphone -->
                        <col>
                            <b-form-group v-if="$store.getters['CheckAction']('Write', undefined, Action)" label-for="checkbox-input" :label="$t('AutoEnablePhone')" label-class="m-0" class="d-flex flex-row-reverse align-items-center m-0 pr-2"
                                v-b-tooltip.hover :title="$t('AutoEnablePhoneTooltip')">
                                <b-form-checkbox id="checkbox-input" v-model="isAutoEnablePhone" :checked-value="true" :unchecked-value="false" switch @change="toggleAutoEnablePhone()"/>
                            </b-form-group>
                        </col>
                        <col>
                            <span v-if="$store.getters['CheckAction']('Write', undefined, Action)" :class="'border-left p-0 my-1 border-' + Variant" />
                        </col>
                        <b-col cols="auto" align-self="center" class="px-1">
                            <!-- Barre d'outils [export/nouveau]-->
                            <b-button-toolbar key-nav :aria-label="$t('ToolbarButtons')">
                                <!-- Bouton d'export -->
                                <!-- $t('ExcelExport') / Exporter les utilisateurs -->
                                <b-button @click="exportToXlsx()" v-b-tooltip.noninteractive.hover :title="$t('ExcelExport')"  variant="outline-dark" class="btn-circle p-0 mx-1" >
                                    <b-icon icon="file-earmark-excel" />
                                </b-button>

                                <!-- Bouton d'ajout d'utilisateur-->
                                <!-- $t('AddUser') / Ajouter un utilisateur -->
                                <b-button v-if="showAddUser" @click="userId = -1; userCreateEdit = true" v-b-tooltip.noninteractive.hover :title="$t('AddUser')"  :variant="Variant" class="btn-circle p-0 mx-1" :disabled="!$store.getters['CheckAction']('Add', undefined, Action)">
                                    <b-icon icon="plus" />
                                </b-button>
                            </b-button-toolbar>
                        </b-col>
                    </b-row>
                </template>
                <b-tab :title="$t('UsersList')" class="h-100" @click="showAddUser = true" >
                    <!-- Liste des utilisateurs -->
                    <UsersList @EditUserEvent="(id, fullname) => { userId = id; userCreateEdit = true; userFullname = fullname;}"/>
                </b-tab>
                <b-tab :title="$t('PhoneAppsList')" :lazy="!isLoadPhones" class="h-100" @click="showAddUser = false">
                    <!-- Liste des applications téléphones DCX Relève -->
                    <PhonesList @loaded="isLoadPhones = true"/>
                </b-tab>
            </b-tabs>

            <!-- Page d'ajout ou modification d'utilisateur -->
            <UserAddEdit v-else :UserId="userId" @CancelEvent="userCreateEdit = false" @SaveEvent="userCreateEdit = false" :FilterTreeStore="filterTreeStore"/>
        </b-card>
    </b-row>
</template>

<script lang="ts">
/** Composant vue.js */
import { Component, InjectReactive, Prop, ProvideReactive, Vue } from 'vue-property-decorator';

/** Import i18n de traduction locaux */
import locales from './locales/UsersManager.json'

/** Composants personnalisés importés */
import UsersList from './UserManager/UsersList.vue';
import PhonesList from './UserManager/PhonesList.vue';
import UserAddEdit from './UserManager/UserAddEdit.vue';

/** Types personnalisés */
import Dictionary from '@/types/Dictionary';
import Download from '@/types/Download';
import DownloadStateEnum from '@/enums/DownloadStateEnum';

/** Libs d'appels webService ServerSide */
import axios from 'axios';
import { API_URL, SELECTOR_FILTER_TREE } from '@/App.settings';

import { FilterTreeBuilder } from '@/components/commons/FilterTreeBuilder';
import SelectModeEnum from '@/enums/SelectModeEnum';

/** Composant d'affichage du menu de gestion des utilisateurs. Il est dédié à l'Administration. */
@Component({
    components:{ 
        PhonesList,
        UsersList,
        UserAddEdit
    },
    i18n: { messages : locales}
})
export default class UsersManager extends Vue {
    private isLoadPhones = false;

    /** Variant du menu */
    @InjectReactive()
    private Variant!: string;

    /** Droit disponible pour l'utilisateur en cours */
    @Prop()
    @ProvideReactive()
    public Action!: string;

    /** Indique le nom du nouveau store Vuex à instancier pour le sélecteur */
    protected filterTreeStore: string = SELECTOR_FILTER_TREE + 'UserAddEdit';

    /** Affiche l'ajout d'utilisateur */
    private showAddUser: boolean = true;

    /** Identifiant de l'utilisateur à éditer ou créer */
    private userId: number = -1;

    /** Nom complet de l'utilisateur à éditer */
    private userFullname: string = "";

    /** Indique si l'on doit afficher l'interface d'ajout ou d'édition utilisateur */
    private userCreateEdit: boolean = false;

    /** Indique si l'activation automatique lors de l'ajout d'un téléphone est activé */
    private isAutoEnablePhone: boolean = false;

    /** Formate le titre de la page en fonction des attributs de la vue (pour que celà soit plus visible qu'une ternaire de ternaire de ternaire ...) */
    private getTitle(){
        if(this.userCreateEdit){
            if(this.userId !== -1){
                if(this.userFullname)
                    return this.$t('Edit') +' "' + this.userFullname + '"'
                else
                    return this.$t('EditUser').toString();
            }
            else
                return this.$t('NewUser').toString();
        }
        else
            return this.$t('UsersManagement').toString();
    }

    /** Permute la fonction d'activation automatique lors de l'ajout d'un téléphone */
    private async toggleAutoEnablePhone(){
        /** Administration/Users/SetAutoActivatePhone */
        await this.$store.dispatch("ShowLoadingMask", this.$t("LoadingAction"));

        const args: Dictionary<any> = {
            Token: Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "",
            AutoActivate : this.isAutoEnablePhone
        };

        await axios.post(API_URL + "Administration/Users/SetAutoActivatePhone", args).then((response) => {
            if(response && response.data){
                this.isAutoEnablePhone = response.data;
            }
        }).catch(err =>{
            console.error(err);
            this.$bvToast.toast(this.$t('AnErrorOccured').toString(), { 
                title: this.$t('Error').toString(),
                autoHideDelay: 10000,
                variant: "danger",
                solid: true,
                appendToast: true,
                toaster: "b-toaster-top-center"
            });
            this.isAutoEnablePhone = !this.isAutoEnablePhone 
        });
        
        await this.$store.dispatch("HideLoadingMask", {});
    }

    /** Export la liste des utilisateurs au format excel */
    private async exportToXlsx() {
        let index: number = this.$store.getters['DownloadingStore/GetLastIndex'];
        let download: Download = {
            Index: ++index,
            Name: "exportUsers"+ ".xlsx",
            Type: ".xlsx",
            State: DownloadStateEnum.InProgress,
            Data: "",
            Direct: false
        };
        this.$store.dispatch('DownloadingStore/AddDownloading', download);

        const args: Dictionary<any> = {};
        args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";

        await axios.post(API_URL + 'Administration/Users/DownloadExport', args, {responseType: 'blob'}).then((response) => {
            let results: Dictionary<any> = response.data;
            this.$store.dispatch('DownloadingStore/AddData', {index, data: results});
            this.$store.dispatch('DownloadingStore/UpdateState', {index, state: DownloadStateEnum.Finished});
        }).catch(err => {
            this.$store.dispatch('DownloadingStore/UpdateState', {index, state: DownloadStateEnum.Error})
            this.$bvToast.toast(this.$t('AnErrorOccuredContactAdmin').toString(), {
                title: this.$t('Error').toString(),
                autoHideDelay: 10000,
                variant: "danger",
                solid: true,
                appendToast: true,
                toaster: "b-toaster-top-center"
            });
        });
    }

    /** Builder d'instance d'arborescence */
    private filterTreeBuilder!: FilterTreeBuilder;

    /** Initialisation du composant */
    async created(){
        /** Recupération de l'arborescence */
        this.filterTreeBuilder = new FilterTreeBuilder(this.$store, this.filterTreeStore);
        await this.filterTreeBuilder.Load(false, undefined, SelectModeEnum.MultiAdvanced);
        
        const args: Dictionary<any> = {};
        args["Token"] = Vue.cookies.get('access_token') != null ? <string>Vue.cookies.get('access_token') : "";

        await axios.post(API_URL + "Administration/Users/GetAutoActivatePhone", args).then((response) => {
            if(response && response.data){
                this.isAutoEnablePhone = response.data;
            }
        }).catch((err) =>{
            console.error(err);
            this.$bvToast.toast(this.$t('AnErrorOccured').toString(), { 
                title: this.$t('Error').toString(),
                autoHideDelay: 10000,
                variant: "danger",
                solid: true,
                appendToast: true,
                toaster: "b-toaster-top-center"
            });
        });
    }

    beforeDestroy() {
        if (this.filterTreeBuilder)
            this.filterTreeBuilder.UnloadFilterTree();
    }
};
</script>

<style scoped>
/** Surcharge local pour avoir un texte correspondant au design exigé */
:deep(.nav-tabs .nav-link:not(.active)) {
  color: var(--text-black);
}

/* Met à jour la couleur de fond du switch du sélecteur de work lorsqu'il est coché */
:deep(.custom-control-input:checked ~ .custom-control-label::before) {
    border-color: var(--Variant) !important;
    background-color: var(--Variant) !important;
}

/* Met à jour la couleur de fond du switch du sélecteur de work lorsqu'il est actif */
:deep(.custom-control-input:active ~ .custom-control-label::before) {
    background-color: var(--Variant) !important;
    border-color: var(--Variant) !important;
}
</style>